import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

export type LegalSlug = 'privacy' | 'terms' | 'responsible-play' | 'compliance';

/**
 * LegalPage — D-13 shared placeholder component.
 *
 * Used by Privacy / Terms / ResponsiblePlay / Compliance route files (each is a
 * one-line wrapper per D-15). Renders:
 *   <h1>{t(`legal.${slug}.title`)}</h1>
 *   <p>{t('legal.placeholder')}</p>
 *
 * The placeholder copy itself (D-14) explicitly says "awaiting lawyer review"
 * in both languages — visitors and screenshot reviewers see the page state
 * without needing to open internal docs (PITFALLS E1/E3/E4 documentation
 * boundary).
 *
 * Phase 3 / pre-launch lawyer review replaces these with real legal content.
 */
export function LegalPage({ slug }: { slug: LegalSlug }): JSX.Element {
  const { t } = useTranslation();
  // Type-checked key: `legal.${slug}.title` is a valid path in mk.json's CustomTypeOptions.
  const titleKey = `legal.${slug}.title` as const;

  return (
    <main className="bg-paper text-ink min-h-[60vh]">
      <article className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-h1 font-sans">{t(titleKey)}</h1>
        <p className="text-body text-dim mt-6 max-w-prose">{t('legal.placeholder')}</p>
      </article>
    </main>
  );
}

export default LegalPage;
